import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  styled,
  Avatar,
  Divider,
  ButtonBase,
  CircularProgress,
  Typography,
} from "@mui/material";
// CUSTOM COMPONENTS
import PopoverLayout from "./PopoverLayout";
import { FlexBox } from "components/flexbox";
import { AvatarLoading } from "components/avatar-loading";
import { H6, Paragraph, Small } from "components/typography";
// CUSTOM DEFINED HOOK
import useAuth from "hooks/useAuth";
import useNavigate from "hooks/useNavigate";
import { Link } from "components/link";
// CUSTOM UTILS METHOD
import { isDark } from "utils/constants";
// API
import UserService from "api/services/UserService";

// STYLED COMPONENTS
const StyledButtonBase = styled(ButtonBase)(({
  theme
}) => ({
  marginLeft: 8,
  borderRadius: 30,
  border: `1px solid ${theme.palette.grey[isDark(theme) ? 800 : 200]}`,
  "&:hover": {
    backgroundColor: theme.palette.action.hover
  }
}));
const StyledSmall = styled(Paragraph)(({
  theme
}) => ({
  fontSize: 13,
  display: "block",
  cursor: "pointer",
  padding: "5px 1rem",
  "&:hover": {
    backgroundColor: theme.palette.action.hover
  }
}));
const DEFAULT_AVATAR = "/static/user/user-11.png";

const ProfilePopover = () => {
  const anchorRef = useRef(null);
  const navigate = useNavigate();
  const { logout, user: authUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState("");
  const [avatarLoading, setAvatarLoading] = useState(true);
  const [fetchingProfile, setFetchingProfile] = useState(false);

  const extractAvatarUrl = useCallback((userData) => {
    if (!userData) return "";
    return (
      userData?.profileImage ||
      userData?.avatarUrl ||
      userData?.avatar ||
      ""
    );
  }, []);

  const withCacheBuster = useCallback((url) => {
    if (!url) return "";
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}v=${Date.now()}`;
  }, []);

  const loadProfile = useCallback(async () => {
    try {
      setFetchingProfile(true);
      const response = await UserService.getProfile();
      if (response?.success) {
        const user = response?.data?.user || response?.data || null;
        setProfile(user);
        const url = withCacheBuster(extractAvatarUrl(user));
        setAvatarUrl(url);
        setAvatarLoading(Boolean(url));
      } else {
        setProfile(authUser || null);
        const url = withCacheBuster(extractAvatarUrl(authUser));
        setAvatarUrl(url);
        setAvatarLoading(Boolean(url));
      }
    } catch (error) {
      setProfile(authUser || null);
      const url = withCacheBuster(extractAvatarUrl(authUser));
      setAvatarUrl(url);
      setAvatarLoading(Boolean(url));
    } finally {
      setFetchingProfile(false);
    }
  }, [authUser, extractAvatarUrl, withCacheBuster]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (open) {
      loadProfile();
    }
  }, [open, loadProfile]);

  const handleMenuItem = path => () => {
    navigate(path);
    setOpen(false);
  };

  const displayUser = profile || authUser || {};
  const displayName =
    displayUser?.displayName ||
    displayUser?.name ||
    displayUser?.username ||
    displayUser?.email ||
    "—";
  const displayEmail = displayUser?.email;
  const displayRole = displayUser?.role || displayUser?.role?.name;

  return <Fragment>
      <StyledButtonBase ref={anchorRef} onClick={() => setOpen(true)}>
        <Box position="relative" display="inline-flex">
          <AvatarLoading
            alt={displayName}
            percentage={avatarLoading ? 40 : 100}
            src={avatarUrl || DEFAULT_AVATAR}
            onImageLoad={() => setAvatarLoading(false)}
            onImageError={() => setAvatarLoading(false)}
            sx={{
              width: 35,
              height: 35,
            }}
          />
          {(avatarLoading || fetchingProfile) && (
            <CircularProgress
              size={28}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                mt: "-14px",
                ml: "-14px",
              }}
            />
          )}
        </Box>
      </StyledButtonBase>

      <PopoverLayout hiddenViewButton maxWidth={230} minWidth={200} popoverOpen={open} anchorRef={anchorRef} popoverClose={() => setOpen(false)} title={<FlexBox alignItems="center" gap={1}>
            <Box position="relative" display="inline-flex">
              <Avatar
                src={avatarUrl || DEFAULT_AVATAR}
                alt={displayName}
                sx={{
                  width: 35,
                  height: 35,
                  "& img": { objectFit: "cover" },
                }}
              />
              {(avatarLoading || fetchingProfile) && (
                <CircularProgress
                  size={26}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    mt: "-13px",
                    ml: "-13px",
                  }}
                />
              )}
            </Box>

            <Box>
              <H6 fontSize={14}>{displayName}</H6>
              {displayEmail && (
                <Small color="text.secondary" display="block">
                  {displayEmail}
                </Small>
              )}
              {displayRole && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {displayRole}
                </Typography>
              )}
            </Box>
          </FlexBox>}>
        <Box pt={1}>
          <StyledSmall onClick={handleMenuItem("/dashboard/admin-list")}>Admin list</StyledSmall>

          <StyledSmall onClick={handleMenuItem("/dashboard/settings")}>Settings</StyledSmall>

          <StyledSmall onClick={handleMenuItem("/dashboard/report-list")}>Report</StyledSmall>

          <StyledSmall onClick={handleMenuItem("/dashboard/subscription-list")}>Subscription</StyledSmall>

          <Divider sx={{
          my: 1
        }} />
          <StyledSmall component={Link} href="https://quickframe-doc.vercel.app/">Documentation</StyledSmall>
          <StyledSmall onClick={handleMenuItem("/components")}>Components</StyledSmall>
          <Divider sx={{
          my: 1
        }} />

          <StyledSmall onClick={logout}>Sign Out</StyledSmall>
        </Box>
      </PopoverLayout>
    </Fragment>;
};
export default ProfilePopover;